// src/services/userService.js
import { db } from '../Firebase/firebase';
import { doc, setDoc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp, writeBatch, arrayUnion, onSnapshot } from 'firebase/firestore';
import { decodeBase64, encodeBase64 } from 'tweetnacl-util';
import nacl from 'tweetnacl';

const SK_STORAGE_KEY_PREFIX = 'chatApp_sk_';

export const storeMySecretKey = (userId, secretKey /* Uint8Array */) => {
    if (!userId) {
        console.error("storeMySecretKey: userId is undefined");
        return;
    }
    localStorage.setItem(`${SK_STORAGE_KEY_PREFIX}${userId}`, encodeBase64(secretKey));
    // console.log(`[storeMySecretKey for ${userId}]: Stored successfully.`);
};

export const getMyStoredSecretKey = async (userId) /*: Promise<Uint8Array | null> */ => {
    if (!userId) {
        console.error("getMyStoredSecretKey: userId is undefined");
        return null;
    }
    const b64SecretKey = localStorage.getItem(`${SK_STORAGE_KEY_PREFIX}${userId}`);
    // console.log(`[getMyStoredSecretKey for ${userId}]: Raw value from localStorage:`, b64SecretKey);
    return b64SecretKey ? decodeBase64(b64SecretKey) : null;
};

export const saveMyPublicKeyToFirestore = async (userId, publicKey /* Uint8Array */) => {
    if (!userId) {
        console.error("saveMyPublicKeyToFirestore: userId is undefined");
        return;
    }
    const userRef = doc(db, 'users', userId);
    try {
        await setDoc(userRef, { publicKey: encodeBase64(publicKey) }, { merge: true });
        // console.log(`[saveMyPublicKeyToFirestore for ${userId}]: Public key saved.`);
    } catch (error) {
        console.error(`[saveMyPublicKeyToFirestore for ${userId}]: Error saving public key:`, error);
        throw error; // Re-throw to indicate failure
    }
};

export const getUserPublicKey = async (userId) /*: Promise<Uint8Array | null> */=> {
    if (!userId) {
        console.warn(`[getUserPublicKey]: userId is undefined`);
        return null;
    }
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists() && userSnap.data()?.publicKey) {
        try {
            return decodeBase64(userSnap.data().publicKey);
        } catch (e) {
            console.error(`[getUserPublicKey for ${userId}]: Error decoding Base64 public key from Firestore:`, e, "Raw data:", userSnap.data().publicKey);
            return null;
        }
    }
    console.warn(`[getUserPublicKey for ${userId}]: Public key not found in Firestore.`);
    return null;
};

// Call this on user registration/first login on a device
export const setupUserKeys = async (userId) => {
    if (!userId) {
        console.error("setupUserKeys: userId is undefined. Cannot proceed.");
        return null;
    }
    console.log(`[setupUserKeys for ${userId}]: Starting key setup...`);

    let localSecretKey = await getMyStoredSecretKey(userId);
    let firestorePublicKey_uint8; // Will hold Uint8Array

    if (localSecretKey) {
        console.log(`[setupUserKeys for ${userId}]: Found secret key in localStorage.`);
        // 1. Secret key exists locally. Now, check Firestore for its public key.
        firestorePublicKey_uint8 = await getUserPublicKey(userId);

        if (firestorePublicKey_uint8) {
            console.log(`[setupUserKeys for ${userId}]: Found public key in Firestore.`);
            // 2. Both keys exist. Verify they form a valid pair.
            try {
                const derivedKeyPair = nacl.box.keyPair.fromSecretKey(localSecretKey);
                const derivedPublicKeyB64 = encodeBase64(derivedKeyPair.publicKey);
                const firestorePublicKeyB64 = encodeBase64(firestorePublicKey_uint8);

                if (derivedPublicKeyB64 === firestorePublicKeyB64) {
                    // 3. Match! Keys are consistent.
                    console.log(`[setupUserKeys for ${userId}]: Keys are consistent and valid.`);
                    return { secretKey: localSecretKey, publicKey: firestorePublicKey_uint8 };
                } else {
                    // 4. Mismatch! Local secret key does not match Firestore public key.
                    // This is a critical inconsistency. The public key in Firestore might be newer
                    // (e.g., from another device or a previous regeneration), or the local secret key is stale.
                    // Safest action: Regenerate keys, making this device authoritative for a new key pair.
                    console.warn(`[setupUserKeys for ${userId}]: MISMATCH! Local secret key does not correspond to Firestore public key. Regenerating keys.`);
                    // Fall through to regeneration logic by clearing localSecretKey
                    localSecretKey = null; // This will trigger the generation block
                    localStorage.removeItem(`${SK_STORAGE_KEY_PREFIX}${userId}`); // Explicitly remove invalid local key
                }
            } catch (e) {
                // Error deriving public key from local secret key (e.g., corrupted key)
                console.error(`[setupUserKeys for ${userId}]: Error deriving public key from local secret key. It might be corrupted. Regenerating. Error:`, e);
                localSecretKey = null; // Trigger regeneration
                localStorage.removeItem(`${SK_STORAGE_KEY_PREFIX}${userId}`);
            }
        } else {
            // 5. Local secret key exists, but NO public key in Firestore.
            // This implies the Firestore part of a previous keypair was lost or never saved correctly.
            // We should use the local secret key to (re-)derive and save the public key.
            console.warn(`[setupUserKeys for ${userId}]: Local secret key found, but no public key in Firestore. Attempting to repair.`);
            try {
                const repairedKeyPair = nacl.box.keyPair.fromSecretKey(localSecretKey);
                await saveMyPublicKeyToFirestore(userId, repairedKeyPair.publicKey);
                console.log(`[setupUserKeys for ${userId}]: Repaired by saving public key (derived from local SK) to Firestore.`);
                return { secretKey: localSecretKey, publicKey: repairedKeyPair.publicKey };
            } catch (e) {
                 console.error(`[setupUserKeys for ${userId}]: Could not repair keys from local SK (possibly corrupted). Regenerating. Error:`, e);
                 localSecretKey = null; // Trigger regeneration
                 localStorage.removeItem(`${SK_STORAGE_KEY_PREFIX}${userId}`);
            }
        }
    }

    // 6. If localSecretKey is null at this point (either never existed, or was cleared due to inconsistency/error)
    if (!localSecretKey) {
        console.log(`[setupUserKeys for ${userId}]: No valid local secret key found or regeneration required. Generating new key pair.`);
        try {
            const keyPair = nacl.box.keyPair();
            storeMySecretKey(userId, keyPair.secretKey);
            await saveMyPublicKeyToFirestore(userId, keyPair.publicKey);
            console.log(`[setupUserKeys for ${userId}]: New keys generated and stored.`);
            return keyPair;
        } catch (error) {
            console.error(`[setupUserKeys for ${userId}]: Failed to generate and store new keys. Error:`, error);
            // Depending on how critical this is, you might want to prevent the user from proceeding
            // or notify them of a problem with their secure communication setup.
            return null; // Or throw the error
        }
    }
    // This part should ideally not be reached if logic is correct,
    // but as a fallback:
    // console.warn(`[setupUserKeys for ${userId}]: Reached end of function with an unresolved key state.`);
    // return { secretKey: localSecretKey, publicKey: firestorePublicKey_uint8 };
};
// Function to get user connection details based on an array of IDs
export const getUserConnections = async (userId) => {
    if (!userId) {
        console.warn("getUserConnections called with no userId");
        return [];
    }

    try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
            const userData = userSnap.data();
            const connectionIdsRaw = userData.connections || []; // Get the raw connections array

            // --- Input Sanitization ---
            // Filter the array to ensure it ONLY contains non-empty strings
            const validConnectionIds = connectionIdsRaw.filter(id => typeof id === 'string' && id.trim() !== '');

            if (validConnectionIds.length === 0) {
                if (connectionIdsRaw.length > 0) {
                    console.warn(`User ${userId} connections array contains invalid data (expected strings):`, connectionIdsRaw);
                }
                return []; // Return empty if no *valid* string IDs found
            }

            // --- Firestore Query ---
            // Handle potential pagination if more than 10 connections
            const connectionsData = [];
            const MAX_IN_QUERIES = 10; // Firestore limit
            const usersRef = collection(db, 'users');

            for (let i = 0; i < validConnectionIds.length; i += MAX_IN_QUERIES) {
                const chunkIds = validConnectionIds.slice(i, i + MAX_IN_QUERIES);
                if (chunkIds.length > 0) {
                    const connectionsQuery = query(usersRef, where('__name__', 'in', chunkIds));
                    const querySnapshot = await getDocs(connectionsQuery);

                    querySnapshot.docs.forEach(docSnap => {
                         if (docSnap.exists()) {
                            connectionsData.push({
                                userId: docSnap.id,
                                name: docSnap.data().name || 'Unknown',
                                profilePicUrl: docSnap.data().profilePicUrl, // Component handles default
                                isOnline: docSnap.data().isOnline || false,
                                lastSeen: docSnap.data().lastSeen || null
                            });
                         }
                    });
                }
            }
            return connectionsData;

        } else {
            console.warn(`User document not found for ID: ${userId}`);
            return [];
        }
    } catch (error) {
        console.error(`Error fetching user connections for ${userId}:`, error);
        if (error instanceof Error && 'code' in error) {
             console.error("Firebase Error Code:", error.code);
             console.error("Firebase Error Message:", error.message);
        }
        return [];
    }
};

// Function to update user's presence status
export const updateUserPresence = async (userId, isOnline) => {
    if (!userId) return;
    const userRef = doc(db, 'users', userId);
    try {
        await updateDoc(userRef, {
            isOnline: isOnline,
            lastSeen: serverTimestamp() // Always update lastSeen timestamp
        });
    } catch (error) {
        if (error.code === 'not-found') {
             console.warn(`User doc ${userId} not found for presence update.`);
        } else {
             console.error("Error updating user presence:", error);
        }
    }
};

// (Optional but recommended) Function to get real-time updates for specific users' presence
export const getUsersPresenceListener = (userIds, callback) => {
    if (!userIds || userIds.length === 0) return () => {};
    const limitedIds = userIds.slice(0, 10); // Handle 'in' query limit

    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('__name__', 'in', limitedIds));

    const unsubscribe = onSnapshot(q, (snapshot) => {
        const presenceData = {};
        snapshot.docs.forEach(doc => {
            presenceData[doc.id] = {
                isOnline: doc.data().isOnline || false,
                lastSeen: doc.data().lastSeen || null
            };
        });
        callback(presenceData);
    }, (error) => {
        console.error("Error fetching real-time presence:", error);
        callback({});
    });

    return unsubscribe;
};


// Example of how to correctly add a connection (store only IDs)
export const addConnection = async (currentUserId, targetUserId) => {
    if (!currentUserId || !targetUserId || currentUserId === targetUserId) {
        console.error("Invalid user IDs for adding connection.");
        return;
    }
    const currentUserRef = doc(db, 'users', currentUserId);
    const targetUserRef = doc(db, 'users', targetUserId);

    try {
        const batch = writeBatch(db);

        // Add target's ID string to current user's connections array
        batch.update(currentUserRef, {
            connections: arrayUnion(targetUserId)
        });

        // Add current user's ID string to target's connections array
        batch.update(targetUserRef, {
            connections: arrayUnion(currentUserId)
        });

        await batch.commit();
        console.log(`Connection between ${currentUserId} and ${targetUserId} added.`);

    } catch (error) {
        console.error("Error adding connection:", error);
        // Handle potential errors like document not found, permissions etc.
    }
};